/**
 * SidebarService - Shared utility for opening berth sidebars across different interfaces
 *
 * Provides a consistent way to open the berth sidebar with the same endpoint
 * used by both the berth map and timeline views.
 */
export class SidebarService {
  /**
   * Opens the berth sidebar with berth slot information
   * @param {Object} params - Parameters for the sidebar
   * @param {string} params.date - Date in YYYY-MM-DD format
   * @param {number} params.berthId - Berth ID (optional if slotId provided)
   * @param {number} params.slotId - Slot ID (optional)
   * @param {Application} params.application - Stimulus application instance (optional)
   * @returns {boolean} Success status
   */
  static async openBerthSidebar({
    date, berthId = null, slotId = null, application = null,
  }) {
    try {
      // Validate required parameters
      if (!date) {
        console.error("SidebarService: date parameter is required");
        return false;
      }

      if (!berthId && !slotId) {
        console.error("SidebarService: either berthId or slotId must be provided");
        return false;
      }

      // Build the sidebar URL
      const url = new URL("/berth_map_slots/sidebar", window.location.origin);
      url.searchParams.append("date", date);

      if (berthId) {
        url.searchParams.append("berth_id", berthId);
      }

      if (slotId) {
        url.searchParams.append("slot_id", slotId);
      }

      // Load content into the side panel frame
      const sidePanelFrame = document.querySelector("turbo-frame#side_panel");
      if (!sidePanelFrame) {
        console.error("SidebarService: side_panel turbo-frame not found in DOM");
        return false;
      }

      sidePanelFrame.src = url.toString();

      // Open the side panel
      const sidePanel = document.querySelector("[data-controller*=\"side-panel\"]");
      if (!sidePanel) {
        console.error("SidebarService: side-panel controller element not found in DOM");
        return false;
      }

      // Use passed application instance (this is the reliable way)
      if (application) {
        const controller = application.getControllerForElementAndIdentifier(sidePanel, "side-panel");
        if (controller && typeof controller.open === "function") {
          controller.open();
          return true;
        }
      }

      // Fallback: Custom event
      sidePanel.dispatchEvent(new CustomEvent("sidebar:open"));
      return true;
    } catch (error) {
      console.error("SidebarService: Error opening sidebar:", error);
      return false;
    }
  }

  /**
   * Formats a date object to YYYY-MM-DD string
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date string
   */
  static formatDate(date) {
    try {
      if (typeof date === "string") return date;
      if (date instanceof Date) {
        return date.toISOString().split("T")[0];
      }
      return date;
    } catch (error) {
      console.error("SidebarService: Error formatting date:", error);
      return date;
    }
  }
}
